import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Mail } from 'lucide-react';
import { Button } from './ui/button';

const PaymentStatus = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment === 'success' || payment === 'cancelled') {
      setStatus(payment);
      // Clean the URL so a refresh won't re-trigger the modal
      params.delete('payment');
      const newSearch = params.toString();
      const newUrl =
        window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  if (!status) return null;

  const isSuccess = status === 'success';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="payment-status-modal"
      onClick={() => setStatus(null)}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-in fade-in zoom-in duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {isSuccess ? (
          <>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="payment-success-title">
              Payment Successful!
            </h2>
            <p className="text-gray-600 mb-4">
              Thank you for your order. Your payment has been received and is being processed.
            </p>
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-1">Confirmation email on the way</p>
                  <p className="text-blue-800">
                    You'll receive an order confirmation at the email you used at checkout within a few
                    minutes. Please check your spam folder if you don't see it.
                  </p>
                </div>
              </div>
            </div>
            <Button
              onClick={() => setStatus(null)}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
              data-testid="payment-success-close-btn"
            >
              Continue Browsing
            </Button>
          </>
        ) : (
          <>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <XCircle className="h-10 w-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="payment-cancelled-title">
              Payment Cancelled
            </h2>
            <p className="text-gray-600 mb-6">
              No worries — your card has not been charged. You can try again whenever you're ready, or
              reach out if you need help completing your order.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => setStatus(null)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700"
                data-testid="payment-cancelled-close-btn"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setStatus(null);
                  document.getElementById('books')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
                data-testid="payment-cancelled-retry-btn"
              >
                Try Again
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentStatus;
